<template>
  <div>
    <div class="widget--toolbar">
      <widget-header
        v-if="!onlyChart"
        title="SMS Volume"
        @remove="$emit('remove-widget')"
      >
        <template v-slot:toolbar>
          <v-overflow-btn
            v-model="selectedDayFilter"
            :items="daysFilterData"
            label="Filter Data"
            item-text="name"
            attach
            item-value="value"
            prepend-inner-icon="mdi-filter-variant"
            outlined
            dense
            short
            hide-details
            item-color="rgba(0,0,0,0.87)"
            class="text-capitalize body-2"
            v-track="'SMS-volume-filter'"
            @change="fetch()"
          />
        </template>
      </widget-header>
    </div>
    <div class="widget--content-no-overflow">
      <v-row>
        <v-col class="py-0">
          <template v-if="chartLoader">
            <ul
            v-if="!onlyChart"
              id="sms-volume-legend"
              class="d-flex d-sm-block justify-space-between overflow-auto pb-2 pl-0 flex-wrap"
            >
              <li
                v-for="(item, index) in legendHeader"
                :key="index"
                style="list-style:none"
                class="mr-2 float-left "
                :class="[item.selected ? 'selected' : '']"
              >
                <div
                  class="d-flex flex-column"
                  @click="customLegend(index)"
                >
                  <div>
                    <div
                      :ref="item.title"
                      :class=" !item.selected
                            ? 'grey--text'
                            : '' "
                      class="mdi legend-eye body-2 float-left text-capitalize "
                    >
                      {{ item.title }}
                    </div>
                    <div
                      class="ml-2 mr-6 legend-slab float-left"
                      :style="`background-color:${item.bkColor}`"
                    />
                  </div>
                  <div :class="!item.selected ? 'grey--text': '' " class="legend-sum">
                    {{ item.sum }}
                  </div>
                </div>
              </li>
            </ul>
            <div
              id="sms-volume-legend"
              ref="smsLegend"
              v-html="legendMarkup"
            />

            <line-chart
              :styles="onlyChart ? styles : graphStyles"
              :options="smsVolumeData.options"
              :chartdata="smsVolumeData.data"
              @legendMarkup="intializeLegend"
            />
          </template>
        </v-col>
      </v-row>
    </div>
  </div>
</template>
<script>
  import { $widgets } from '@core/services/'
  import LineChart from '@core/components/charts/LineChart.vue'
  import WidgetHeader from '@core/components/widgets/WidgetHeader'
  import commonFunctions from '@core/commonFunctions'

  export default {
    components: {
      LineChart,
      WidgetHeader,
    },
    /**
    |--------------------------------------------------
    | Props
    |--------------------------------------------------
    */
    props: {
        onlyChart: { type: Boolean, default: false },
        fetchRecords: { type: Boolean, default: true },
        recordDays: { type: Number, default: 7 },
        styles: { type: Object, default: () => ({}) }
      },
    data () {
      return {
        legendMarkup: null,
        legendHeader: [
          { 
            id: 1,
            title: 'outbound', 
            selected: true,
            bkColor: '#03B6FF',
            sum: 0
          },
          { 
            id: 3,
            title: 'inbound',
            selected: true,
            bkColor: '#F53BAD',
            sum: 0
          },
        ],
        chartRef: null,
        loading: true,
        chartLoader: false,
        selectedDayFilter: 7,
        daysFilterData: [
          { name: 'Last 7 days', value: 7 },
          { name: 'Last 30 days', value: 30 },
          { name: 'Last 90 days', value: 90 },
          { name: 'Year to date', value: 365 },
        ],
        smsVolumeData: {},
        tempDataStore: {},
        nOfMonInRange: 12,
        yearToDate: 365,
      }
    },
    computed: {
      chartTickMarkLength(){
        return this.onlyChart ? 0 : 1
      },
      graphStyles () {
        let height = '220px'
        if (this.$vuetify.breakpoint.xsOnly) {
          height = '170px'
        }

        if (this.$vuetify.breakpoint.smOnly) {
          height = '255px'
        }

        return {
          height: height,
          width: '100%',
        }
      },
    },
    watch: {
      fetchRecords(){
        this.selectedDayFilter = this.recordDays
        this.fetch()
      }
    },
    mounted () {
      this.tempDataStore = {
        ...commonFunctions.prepareSkeletonData(this.$dateEST()),
      }
      this.smsVolumeData = {
        options: {
          maintainAspectRatio: false,
          responsive: true,
          legend: {
            display: false,
            align: 'start',
            fullWidth: true,
            labels: {
              fontSize: 12,
              fontFamily:
                "'Open Sans', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
            },
          },
          legendCallback: function (chart) {
            const legendHtml = []
            return legendHtml.join('')
          },
          elements: {
            point: { radius: 1 },
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: true,
                  tickMarkLength: this.chartTickMarkLength,
                },
                ticks: {
                  display: !this.onlyChart,
                },
              },
            ],
            yAxes: [
              {
                gridLines: {
                  display: true,
                  drawBorder: true, 
                },
                ticks: {
                  display: true,
                  min: 0,
                  precision: 0,
                  callback: function (value, index, values) {
                    return commonFunctions.numberWithCommas(value)
                  },
                },
              },
            ],
          },
          tooltips: {
            mode: 'index',
            backgroundColor: '#E8E5E5',
            titleFontColor: 'black',
            bodyFontColor: 'black',
            bodyAlign: 'left',
            callbacks: {
              label: function (tooltipItems, data) {
                return data.datasets[tooltipItems.datasetIndex].data[
                  tooltipItems.index
                ] === null
                  ? data.datasets[tooltipItems.datasetIndex].label + ' : ' + 0
                  : data.datasets[tooltipItems.datasetIndex].label +
                    ' : ' +
                    data.datasets[tooltipItems.datasetIndex].data[
                      tooltipItems.index
                    ].toLocaleString()
              },
            },
          },
        },
        data: {
          labels: [],
          datasets: [
            {
              label: 'Outbound',
              data: [],
              fill: false,
              borderWidth: 1,
              pointwidth: 3,
              backgroundColor: '#03b6fc',
              pointBorderWidth: 4,
              pointBorderColor: '#03b6fc',
              borderColor: '#03b6fc',
              hoverBorderColor: '#02B6FC',
              pointHoverBorderColor: '#fff',
              pointHoverBackgroundColor: '#03B6FC',
              pointHoverBorderWidth: 5,
              pointStyle: 'circle',
              hidden: false,
            },
            {
              label: 'Inbound',
              usePointStyle: true,
              data: [],
              fill: false,
              borderWidth: 1,
              pointwidth: 3,
              backgroundColor: '#F53BAD',
              pointBorderWidth: 5,
              pointBorderColor: '#F53BAD',
              borderColor: '#F53BAD',
              hoverBorderColor: 'white',
              pointHoverBorderColor: '#fff',
              pointHoverBackgroundColor: '#F53BAD',
              pointHoverBorderWidth: 5,
              hidden: false,
            },
          ],
        },
      }
      
      // Override value of selectedDayFilter when only chart is to be shown
      if (this.onlyChart) this.selectedDayFilter = this.recordDays
      
      this.fetch()
    },
    methods: {
      customLegend (index) {
        this.legendHeader[index].selected = !this.legendHeader[index].selected;
        this.smsVolumeData.data.datasets[index].hidden = !this.smsVolumeData.data.datasets[index].hidden
        if (this.chartRef) {
          this.chartRef.renderChart(
            this.smsVolumeData.data,
            this.smsVolumeData.options
          )
        }
      },
      lastNDays (alterFilter) {
        var result = []
        let daysLength = this.selectedDayFilter === 90 ? this.selectedDayFilter + 1 : this.selectedDayFilter
        for (var i = 0; i < daysLength; i++) {
          var d = new Date()
          d.setDate(d.getDate() - i)
          result.push(
            this.$options.filters.formatDate(
              d,
              alterFilter ? 'YYYY-MM-DD' : 'DD MMM'
            )
          )
        }
        return result
      },
      fetch () {
        this.chartLoader = false
        this.smsVolumeData.data.datasets[0].data = []
        this.smsVolumeData.data.datasets[1].data = []
        this.smsVolumeData.data.labels = []
        $widgets
          .getSMSVolume({ params: { days_count: this.selectedDayFilter } })
          .then(response => {
            let sumInbound = 0
            let sumOutbound = 0
            response.data.data.map(resData => {
              sumInbound += resData.inbound
              let outboundCount = resData.outbound
              sumOutbound += outboundCount
              if (this.selectedDayFilter === this.yearToDate) {
                const monthIndex = commonFunctions.getMonthIndex(
                  resData.report_date,
                  this.tempDataStore
                )
                if (monthIndex >= 0) {
                  var tempDataArray = [
                    outboundCount,
                    resData.inbound
                  ]
                  //loop based on legends in chart
                  for (var i = 0; i <= 1; i++) {
                    this.smsVolumeData.data.datasets[i].data[monthIndex] = !this.smsVolumeData.data.datasets[i].data[monthIndex]
                      ? tempDataArray[i]
                      : this.smsVolumeData.data.datasets[i].data[monthIndex] +
                        (tempDataArray[i] ? tempDataArray[i] : 0)
                  }
                }
              } else {
                this.lastNDays(true)
                  .reverse()
                  .map((label, index) => {
                    
                    if (resData.report_date === label) {
                      this.smsVolumeData.data.datasets[0].data[
                        index
                      ] = (outboundCount)
                        ? (outboundCount) !== null || undefined
                          ? (outboundCount)
                          : 0
                        : 0
                      this.smsVolumeData.data.datasets[1].data[
                        index
                      ] = resData.inbound
                        ? resData.inbound !== null || undefined
                          ? resData.inbound
                          : 0
                        : 0
                    }
                  })
              }
            })
            this.legendHeader[0].sum = isNaN(sumOutbound) ? 0 : sumOutbound
            this.legendHeader[1].sum = isNaN(sumInbound) ? 0 : sumInbound
            if (this.selectedDayFilter === this.yearToDate) {
              for (var i = 0; i <= this.nOfMonInRange; i++) {
                if (
                  typeof this.smsVolumeData.data.datasets[0].data[i] ===
                  'undefined'
                ) {
                  this.smsVolumeData.data.datasets[0].data[i] = 0
                }
                if (
                  typeof this.smsVolumeData.data.datasets[1].data[i] ===
                  'undefined'
                ) {
                  this.smsVolumeData.data.datasets[1].data[i] = 0
                }
              }
            } else {
              this.lastNDays(true)
                .reverse()
                .map((label, index) => {
                  if (
                    typeof this.smsVolumeData.data.datasets[0].data[index] ===
                    'undefined'
                  ) {
                    this.smsVolumeData.data.datasets[0].data[index] = 0
                  }
                  if (
                    typeof this.smsVolumeData.data.datasets[1].data[index] ===
                    'undefined'
                  ) {
                    this.smsVolumeData.data.datasets[1].data[index] = 0
                  }
                })
            }
              this.smsVolumeData.data.labels =
              this.selectedDayFilter === this.yearToDate
                ? this.tempDataStore.labels
                : this.lastNDays(false).reverse()
            this.chartLoader = true
          })
          .catch(error => {
            this.chartLoader = false
            this.error = error
            this.chartLoader = true
          })
      },
      intializeLegend (data) {
        this.chartRef = data
        this.legendMarkup = this.chartRef.$data._chart.generateLegend()
      },
    },
  }
</script>
<style scoped>

#sms-volume-legend li{
  cursor: pointer;
}

#sms-volume-legend li:hover{
  transform: scale(1.05);
  transition: all 100ms ease-in-out;
}

#sms-volume-legend li:first-child:hover{
  padding-left: 4px!important;
}

#sms-volume-legend li:not(.selected) .legend-slab{
  background-color: lightgrey !important;
}

#sms-volume-legend .legend-slab {
  height: 14.67px;
  width: 39px;
  border-radius: 4px;
  margin-top: 3px;
}
.strike {
  text-decoration: line-through;
}
.widget--content-no-overflow {
  webkit-box-flex: 1;
  flex: 1;
}

.legend-sum
{
  font-size: 24px;
  font-weight: 600;
}


</style>